/**
 * bri-size-chart: knop en venster voor de maattabel (zie blocks/bri-size-chart.liquid).
 * - De knop wordt in het label van de maatoptie van de variantkiezer gezet en blijft daar na elke herbouw van de kiezer.
 * - Het venster is een native <dialog>: Escape sluit, een klik naast het venster sluit, de focus gaat terug naar de knop.
 */

/** Namen die een maatoptie kunnen aanduiden (hoofdletterongevoelig). */
(() => {
const SIZE_OPTION_NAME = /^(sizes?|maten?|maat|tailles?|gr(o|ö|oe)(ss|ß)e|storlek|tallas?|taglia|taglie|tamanho|tama(n|ñ)o)$/i;

class BriSizeChart extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #trigger = null;
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  /** @type {MutationObserver | null} */
  #observer = null;
  #abort = new AbortController();

  connectedCallback() {
    this.#trigger = this.querySelector('[data-bri-open]');
    this.#dialog = this.querySelector('dialog');
    if (!this.#trigger || !this.#dialog) return;

    const { signal } = this.#abort;
    this.#trigger.addEventListener('click', () => this.#open(), { signal });
    this.#dialog.addEventListener(
      'click',
      (event) => {
        // een klik op de achtergrond komt aan als een klik op de dialog zelf
        if (event.target === this.#dialog || (event.target instanceof Element && event.target.closest('[data-bri-close]'))) {
          this.#dialog?.close();
        }
      },
      { signal }
    );
    this.#dialog.addEventListener('close', () => this.#afterClose(), { signal });

    const picker = this.closest('.shopify-section')?.querySelector('variant-picker');
    if (picker) {
      this.#place(picker);
      this.#observer = new MutationObserver(() => this.#place(picker));
      this.#observer.observe(picker, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#observer?.disconnect();
    this.#observer = null;
    document.documentElement.style.removeProperty('overflow');
  }

  #open() {
    if (!this.#dialog || this.#dialog.open) return;
    this.#dialog.showModal();
    this.#trigger?.setAttribute('aria-expanded', 'true');
    document.documentElement.style.overflow = 'hidden';
  }

  #afterClose() {
    this.#trigger?.setAttribute('aria-expanded', 'false');
    document.documentElement.style.removeProperty('overflow');
    this.#trigger?.focus();
  }

  /**
   * Zet de knop in het label van de maatoptie. Geen maatoptie gevonden: de knop blijft waar hij is.
   * @param {Element} picker
   */
  #place(picker) {
    if (!this.#trigger) return;
    for (const legend of picker.querySelectorAll('fieldset legend')) {
      const first = legend.firstChild;
      const name = (first && first.textContent ? first.textContent : legend.textContent || '').trim();
      if (!SIZE_OPTION_NAME.test(name) && !/size/i.test(name)) continue;
      if (this.#trigger.parentElement !== legend) {
        legend.classList.add('bri-size-legend');
        legend.append(this.#trigger);
      }
      return;
    }
  }
}

if (!customElements.get('bri-size-chart')) customElements.define('bri-size-chart', BriSizeChart);
})();
